
/** One configuration row on a property, as returned by the property API. */
export interface PropertyPriceItem {
  _id?: string;
  bhkType?: string;
  price?: number | string;
  areaInSq?: number | string;
  averagePrice?: number | string;
  propertyStructureType?: string;
}

/** The subset of the property API record the CI flow reads. */
export interface PropertyApiRecord {
  _id: string;
  name: string;
  propertyType?: string;
  listingType?: string;
  /** Search API sends the listing type ("Resale") here. */
  type?: string;
  landArea?: number | string;
  superBuiltUpArea?: number | string;
  location?: string;
  builder?: string;
  microMarket?: { micromarketName?: string; cluster?: string } | null;
  price?: PropertyPriceItem[] | null;
  ciIntelligence?: {
    benchmarkPsf?: number | string | null;
    pricing?: { benchmarkPsf?: number | string | null };
    revisedBenchmark?: number | null;
    lastFiveTransactions?: { price?: number | string | null; source?: string | null; date?: string | null }[] | null;
  } | null;
  cipIntelligence?: PropertyApiRecord['ciIntelligence'];
  ciPublicReport?: PropertyApiRecord['ciIntelligence'];
}

/** A search hit, flattened for the result cards and form population. */
export interface InventoryProperty {
  id: string;
  propertyName: string;
  propertyType: string;
  listingType: string;
  propertyStructureTypes: string[];
  microMarket: string;
  cluster: string;
  location: string;
  configuration: string;
  /** "3 BHK · 1,710 sq.ft" style summary of every configuration. */
  configurations: string[];
  size: number | null;
  /** Dedicated plot area; never inferred from a listing's SBA. */
  landArea: number | null;
  /** Total building area across floors, supplied separately from land area. */
  builtUpArea: number | null;
  quotedPrice: number | null;
  /** Inventory's calculated rate, or the quoted price divided by area. */
  averagePsf: number | null;
  /** Lowest–highest price across configurations, when there is more than one. */
  priceMin: number | null;
  priceMax: number | null;
  /** Generated CI history, when the search endpoint includes it. */
  ciTransactions: { price: number; source: string; date: string }[];
  ciBenchmarkPsf: number | null;
}

/** CI reports expose formatted pricing; calculator records expose numeric rates. */
export function ciBenchmarkRate(ci: any): number | null {
  for (const value of [ci?.revisedBenchmark, ci?.benchmarkPsf, ci?.pricing?.benchmarkPsf, ci?.benchmarkPSF]) {
    const normalized = typeof value === 'string'
      ? value.trim().replace(/^₹\s*/, '').replace(/\s*\/\s*sq\.?\s*ft\.?$/i, '').replace(/,/g, '').trim()
      : value;
    const rate = Number(normalized);
    if (Number.isFinite(rate) && rate > 0) return rate;
  }
  return null;
}

function toNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** "Whitefield (C2)" → "Whitefield" — same clean-up the properties list applies. */
function cleanCluster(cluster?: string | null): string {
  return (cluster ?? '').replace(/\(C\d+\)/g, '').trim();
}

export function toInventoryProperty(record: PropertyApiRecord): InventoryProperty {
  const prices = record.price ?? [];
  const first = prices[0];
  const amounts = prices.map((p) => toNumber(p.price)).filter((n): n is number => n !== null);

  const size = toNumber(first?.areaInSq);
  const quotedPrice = toNumber(first?.price);
  const ci = record.ciIntelligence ?? record.cipIntelligence ?? record.ciPublicReport;
  const ciTransactions = (ci?.lastFiveTransactions ?? [])
    .map((transaction) => ({
      price: toNumber(transaction.price),
      source: transaction.source?.trim() ?? '',
      date: transaction.date ?? '',
    }))
    .filter((transaction): transaction is { price: number; source: string; date: string } => !!transaction.price && !!transaction.date);

  return {
    id: record._id,
    propertyName: record.name ?? '',
    propertyType: record.propertyType ?? '',
    listingType: record.listingType ?? record.type ?? '',
    propertyStructureTypes: prices
      .map((price) => price.propertyStructureType ?? '')
      .filter(Boolean),
    microMarket: record.microMarket?.micromarketName || record.location || '',
    cluster: cleanCluster(record.microMarket?.cluster),
    location: record.location ?? '',
    // Plots and houses carry no BHK type; fall back to the structure/property
    // type so the CI request always has a configuration.
    configuration: first?.bhkType || first?.propertyStructureType || record.propertyType || '',
    configurations: prices
      .filter((p) => !!p.bhkType)
      .map((p) => {
        const area = toNumber(p.areaInSq);
        return area ? `${p.bhkType} · ${area.toLocaleString('en-IN')} sq.ft` : `${p.bhkType}`;
      }),
    size,
    landArea: toNumber(record.landArea),
    // Never fall back to the plot size: a house's built-up area must be entered.
    builtUpArea: toNumber(record.superBuiltUpArea),
    quotedPrice,
    averagePsf: toNumber(first?.averagePrice) ?? (size && quotedPrice ? quotedPrice / size : null),
    priceMin: amounts.length ? Math.min(...amounts) : null,
    priceMax: amounts.length ? Math.max(...amounts) : null,
    ciTransactions,
    ciBenchmarkPsf: ciBenchmarkRate(ci),
  };
}

/** Listings are named "Property · configuration" on the form (sent for every category). */
export function listingUnitName(item: InventoryProperty): string {
  return [item.propertyName, item.configuration].filter(Boolean).join(' · ');
}
